import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { JsonLd, getBreadcrumbSchema, getWebPageSchema, getOrganizationSchema, getFAQSchema } from '@/components/feature/JsonLd';
import { aboutPageData, faqData } from '@/mocks/videos';

export default function AboutPage() {
  const pageTitle = '关于我们 - 桃花视频 | 品牌介绍 · 口碑信誉 · 联系方式';
  const pageDesc = '了解桃花视频的品牌故事、发展历程和核心优势。桃花视频成立于2018年，是国内领先的在线视频流媒体平台，累计用户超5000万，提供海量高清影视资源。';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <ScrollToTop />
      <JsonLd data={getOrganizationSchema()} />
      <JsonLd data={getWebPageSchema(pageTitle, pageDesc, '/about')} />
      <JsonLd data={getBreadcrumbSchema([
        { name: '桃花视频', item: '/' },
        { name: '关于我们', item: '/about' },
      ])} />
      <JsonLd data={getFAQSchema(faqData)} />

      <Header />

      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50" aria-label="品牌介绍">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-primary-500 rounded-2xl">
                <i className="ri-play-circle-fill text-3xl text-white"></i>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-3">
                关于桃花视频
              </h1>
              <p className="text-lg text-accent-500 font-medium">{aboutPageData.slogan}</p>
            </div>

            <div className="prose prose-base max-w-none text-foreground-700 leading-relaxed mb-10">
              <p className="text-base md:text-lg">{aboutPageData.intro}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <div className="bg-background-100 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100">
                    <i className="ri-focus-2-line text-lg text-accent-500"></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900">我们的使命</h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutPageData.mission}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className="ri-eye-line text-lg text-primary-500"></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900">我们的愿景</h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutPageData.vision}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="平台数据">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                平台数据
              </h2>
              <p className="text-sm text-foreground-500">用数据说话，值得信赖</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {aboutPageData.stats.map((stat) => (
                <div key={stat.label} className="text-center bg-background-50 rounded-lg p-5 md:p-6">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-background-200">
                    <i className={`${stat.icon} text-xl text-primary-500`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50" aria-label="发展历程">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                发展历程
              </h2>
              <p className="text-sm text-foreground-500">一路走来，感谢有你</p>
            </div>
            <div className="relative">
              <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {aboutPageData.milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="flex items-center gap-4 md:w-1/2 md:justify-end">
                      <div className="w-10 h-10 shrink-0 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-bold z-10">
                        {item.year.slice(2)}
                      </div>
                    </div>
                    <div className="pl-14 md:pl-0 md:w-1/2">
                      <div className="bg-background-100 rounded-lg p-5">
                        <span className="text-xs font-bold text-primary-500">{item.year}</span>
                        <h3 className="text-base font-semibold text-foreground-900 mt-1 mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="常见问题">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">快速解答您的疑问</p>
            </div>
            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors duration-200 list-none">
                    <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-8">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50" aria-label="联系我们">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">期待您的来电与来信</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-background-100 rounded-lg p-6 flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-mail-line text-lg text-primary-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-600">{aboutPageData.contact.email}</p>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg p-6 flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-phone-line text-lg text-accent-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
                  <p className="text-sm text-foreground-600">{aboutPageData.contact.phone}</p>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg p-6 flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-map-pin-line text-lg text-primary-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                  <p className="text-sm text-foreground-600">{aboutPageData.contact.address}</p>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg p-6 flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-wechat-line text-lg text-accent-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">社交媒体</h3>
                  <p className="text-sm text-foreground-600">微信：{aboutPageData.contact.wechat}</p>
                  <p className="text-sm text-foreground-600">微博：{aboutPageData.contact.weibo}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}